import { TriangleAlert } from 'lucide-react';

import type { GapDraftView, GapRunValidation } from '@momentum/types';
import { GapRunChronologyVerdict } from '@momentum/types';

import { SCORE_LABEL, scoreDeltas, shortScore } from '../../lib/scores';
import { EvidenceList } from '../EvidenceList';
import { FieldInfo } from '../FieldInfo';

/**
 * `CURRENT_FACT` is the only verdict that leaves the claim standing on its own
 * facts. Every other value — including `CHRONOLOGY_UNKNOWN` — means a reviewer
 * has to go and check the timeline themselves.
 */
function chronologyTone(verdict: GapRunChronologyVerdict): string {
  return verdict === GapRunChronologyVerdict.CURRENT_FACT ? 'emerald' : 'amber';
}

/**
 * What adjudication did to the finder's four scores, and the confidence it was
 * claimed with.
 *
 * Every axis renders `before → after`, including the ones the validator left
 * alone. The rail used to collapse those to the word "unchanged" — which said
 * what the validator did but hid what the gap actually scores, and the score is
 * what a reviewer opens this block for. `data-direction` still carries the move,
 * so an unmoved row recedes without going blank.
 *
 * Confidence has one value rather than a pair because the validator never
 * re-scores it: it belongs to the finder, and the verdict above is the
 * validator's answer to it.
 */
function ScoreDeltas({ draft }: { draft: GapDraftView }) {
  const validation = draft.source.validation;
  if (validation === undefined) return null;

  return (
    <div className="gr-deltas">
      {scoreDeltas(draft.source, validation).map((delta) => (
        <div
          className="gr-delta"
          data-direction={delta.direction}
          key={delta.axis}
        >
          <span>{SCORE_LABEL[delta.axis]}</span>
          <span className="gr-delta-move">
            {shortScore(delta.before)} <i>→</i> {shortScore(delta.after)}
          </span>
        </div>
      ))}
      <div className="gr-delta" data-direction="none">
        <span>confidence (finder)</span>
        <span className="gr-delta-move">
          {shortScore(draft.source.confidence)}
        </span>
      </div>
    </div>
  );
}

function ValidatorFacts({ validation }: { validation: GapRunValidation }) {
  return (
    // Stacked rather than key-on-the-left: three of these four are sentences
    // the validator wrote, and a two-column row leaves them a third of the
    // rail to wrap in.
    <div className="gr-facts gr-facts-stacked">
      {validation.cheapestCheck !== undefined && (
        <div className="gr-fact">
          <span>
            cheapest check
            <FieldInfo path="source.validation.cheapestCheck" />
          </span>
          <span className="gr-fact-v">{validation.cheapestCheck}</span>
        </div>
      )}
      {validation.baseRate !== undefined && (
        <div className="gr-fact">
          <span>
            base rate
            <FieldInfo path="source.validation.baseRate" />
          </span>
          {/* A written ratio ("absent-and-hurt 3/14"), not a number — the
              denominator is the part that makes it mean anything. */}
          <span className="gr-fact-v mono">{validation.baseRate}</span>
        </div>
      )}
      {validation.precedentCheck !== undefined && (
        <div className="gr-fact">
          <span>
            precedent check
            <FieldInfo path="source.validation.precedentCheck" />
          </span>
          <span className="gr-fact-v">{validation.precedentCheck}</span>
        </div>
      )}
      <div className="gr-fact">
        <span>
          chronology verdict
          <FieldInfo path="source.validation.chronologyVerdict" />
        </span>
        <span className="gr-fact-v">
          <span
            className="gr-chip"
            data-tone={chronologyTone(validation.chronologyVerdict)}
          >
            {validation.chronologyVerdict}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * Validator tab — the adjudication, in the order SPEC §4.4 fixes.
 *
 * Verdict first, then what it did to the finder's scores, then the evidence
 * AGAINST before the evidence for. The order is deliberate: a reviewer reading a
 * confirmed gap should meet the case against it before the case for it, because
 * the case for it is the claim they have already read in the reader.
 *
 * A gap can arrive unadjudicated — a validator batch whose verdict set did not
 * match the batch it was sent, twice. That is a pipeline failure the reviewer
 * needs to see, not an empty tab.
 */
export function ValidatorTab({ draft }: { draft: GapDraftView }) {
  const validation = draft.source.validation;

  if (validation === undefined) {
    return (
      <div className="gr-note" data-tone="amber">
        <TriangleAlert />
        <div>
          <strong>Never adjudicated.</strong> No validator verdict reached this
          gap, so nothing here has been checked against the evidence — the claim
          is the finder&apos;s alone. The run audit&apos;s coverage section
          names the batch that failed.
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="gr-verdict" data-verdict={validation.verdict}>
          <TriangleAlert /> {validation.verdict}
        </div>
        <p className="gr-headline">{validation.headline}</p>
      </div>

      <ScoreDeltas draft={draft} />

      <div>
        <h3 className="gr-insp-h">Evidence against</h3>
        {validation.evidenceAgainst.length > 0 ? (
          <EvidenceList evidence={validation.evidenceAgainst} marker="—" />
        ) : (
          <div className="gr-empty">Nothing recorded against this claim.</div>
        )}
      </div>

      <div>
        <h3 className="gr-insp-h">Evidence for</h3>
        {validation.evidenceFor.length > 0 ? (
          <EvidenceList
            evidence={validation.evidenceFor}
            marker="+"
            tone="for"
          />
        ) : (
          <div className="gr-empty">Nothing recorded for this claim.</div>
        )}
      </div>

      <ValidatorFacts validation={validation} />

      <details className="gr-fold">
        <summary>Search method disclosure</summary>
        <div className="gr-fold-body">{validation.searchMethodDisclosure}</div>
      </details>
    </>
  );
}
